import React, { useState, useEffect, useCallback } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Typography, 
  Button,
  Space,
  Spin,
  message,
  InputNumber
} from 'antd';
import { 
  EnvironmentOutlined, 
  SearchOutlined, 
  AimOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { debounce } from 'lodash';
import { apiService } from '../../utils/apiService';
import { API_CONFIG } from '../../utils/constants';
import { getCurrentLocation } from '../../utils/locationUtils';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Location Step Component
 * Handles address selection with custom locations and GoogleMaps integration
 */
const LocationStep = ({ form, formData, updateFormData, loading }) => {
  const [addressOptions, setAddressOptions] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [customLocations, setCustomLocations] = useState([]);
  const [loadingLocation, setLoadingLocation] = useState(false);

  /**
   * Create debounced address search function
   */
  const debouncedAddressSearch = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm && searchTerm.length > 2) {
        await searchAddresses(searchTerm);
      } else {
        setAddressOptions([]);
      }
    }, 500), [customLocations]
  );

  const searchAddresses = async (searchTerm) => {
    setLoadingAddresses(true);
    try {
      try {
        const encodedAddress = encodeURIComponent(searchTerm);
        const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.GOOGLEGEOCODE.AUTOCOMPLETE}?address=${encodedAddress}`;
        
        const googleMapsResponse = await fetch(apiUrl);
        const googleMapsData = await googleMapsResponse.json();

        if (googleMapsData.suggestions && Array.isArray(googleMapsData.suggestions)) {
          const googleMapsResults = googleMapsData.suggestions
            .slice(0, 5)
            .map(item => ({
              type: 'google',
              id: item.placePrediction.placeId,
              title: item.placePrediction.text.text,
              address: item.placePrediction.text.text,
            }));
          
          setAddressOptions(googleMapsResults);
        }
      } catch (googleMapsError) {
        console.warn('Google Maps search failed:', googleMapsError);
      }
    } catch (error) {
      console.error('Error searching addresses:', error);
      message.error('Failed to search addresses');
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleReverseGeocodeByPlaceId = async (placeId) => {
    try {
      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.GOOGLEGEOCODE.GEOCODE_BY_PLACE_ID}?placeId=${placeId}`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return null;
    }
  };

  /**
   * Handle address selection
   */
  const handleAddressSelect = async (value, option) => {
    const reverseGeocodeData = await handleReverseGeocodeByPlaceId(value);
    const selectedOption = reverseGeocodeData.results[0];
    console.log(selectedOption);
    if (selectedOption) {
      updateFormData({
        address: selectedOption.custom_formatted_address,
        latitude: selectedOption.geometry.location.lat,
        longitude: selectedOption.geometry.location.lng
      });
      
      // Update Antd form fields to sync UI
      form.setFieldValue('latitude', selectedOption.geometry.location.lat);
      form.setFieldValue('longitude', selectedOption.geometry.location.lng);
    }
  };

  const handleReverseGeocodeByCoordinates = async (lat, lng) => {
    try {
      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.GOOGLEGEOCODE.REVERSE_GEOCODE}?latitude=${lat}&longitude=${lng}`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return null;
    }
  };

  /**
   * Handle manual address input
   */
  const handleAddressChange = (value) => {
    updateFormData({
      address: value
    });
    debouncedAddressSearch(value);
  };

  /**
   * Handle latitude change and trigger reverse geocoding
   */
  const handleLatitudeChange = (value) => {
    updateFormData({
      latitude: value
    });
    
    if (value && formData.longitude) {
      debouncedReverseGeocode(value, formData.longitude);
    }
  };

  /**
   * Handle longitude change and trigger reverse geocoding
   */
  const handleLongitudeChange = (value) => {
    updateFormData({
      longitude: value
    });
    
    if (value && formData.latitude) {
      debouncedReverseGeocode(formData.latitude, value);
    }
  };

  /**
   * Debounced reverse geocoding function
   */
  const debouncedReverseGeocode = useCallback(
    debounce(async (lat, lng) => {
      await performReverseGeocode(lat, lng);
    }, 1000),
    [customLocations]
  );

  /**
   * Perform reverse geocoding using GoogleMaps and custom locations
   */
  const performReverseGeocode = async (lat, lng) => {
      // Try GoogleMaps reverse geocoding if API key is available
    try {
      const response = await handleReverseGeocodeByCoordinates(lat, lng);
      const data = response?.results?.[0];

      console.log(data);
      if (data) {
        const resolvedAddress = data.custom_formatted_address || data.placeId;
        updateFormData({
          address: resolvedAddress
        });
        form.setFieldValue('address', resolvedAddress);
      }
    } catch (error) {
      console.warn('GoogleMaps reverse geocoding failed:', error);
    }
  };

  /**
   * Use current location
   */
  const useCurrentLocation = async () => {
    setLoadingLocation(true);
    try {
      const position = await getCurrentLocation();
      
      // Update internal form data
      updateFormData({
        latitude: position.latitude,
        longitude: position.longitude
      });
      
      // Sync with Antd form fields to update UI
      form.setFieldValue('latitude', position.latitude);
      form.setFieldValue('longitude', position.longitude);
      
      // Perform reverse geocoding to get address
      await performReverseGeocode(position.latitude, position.longitude);
      
      message.success('Current location retrieved successfully');
    } catch (error) {
      console.error('Error getting current location:', error);
      message.error('Failed to get current location. Please check location permissions.');
    } finally {
      setLoadingLocation(false);
    }
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: '24px' }}>
        <EnvironmentOutlined style={{ marginRight: '8px' }} />
        Location Information
      </Title>
      
      <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
        Enter the breakdown location address and coordinates
      </Text>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '16px'
      }}>
        <div style={{ width: '100%' }}>
          <Form.Item
            label="Address"
            name="address"
            rules={[
              { required: true, message: 'Please enter or select an address' }
            ]}
          >
            <Select
              showSearch
              filterOption={false}
              placeholder="Search for an address or location"
              loading={loadingAddresses}
              value={formData.address}
              onSearch={handleAddressChange}
              onSelect={handleAddressSelect}
              notFoundContent={loadingAddresses ? <Spin size="small" /> : null}
              suffixIcon={<SearchOutlined />}
            >
              {addressOptions.map(option => (
                <Option key={option.id} value={option.id}>
                  {option.title}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'flex-end'
        }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <Form.Item
              label="Latitude"
              name="latitude"
              rules={[
                { required: true, message: 'Please enter latitude' },
                { 
                  pattern: /^-?([1-8]?[0-9](\.[0-9]+)?|90(\.0+)?)$/, 
                  message: 'Please enter a valid latitude (-90 to 90)' 
                }
              ]}
            >
              <InputNumber
                placeholder="e.g., -26.2041"
                style={{ width: '100%' }}
                step={0.000001}
                precision={6}
                onChange={handleLatitudeChange}
              />
            </Form.Item>
          </div>
          
          <div style={{ flex: '1', minWidth: '200px' }}>
            <Form.Item
              label="Longitude"
              name="longitude"
              rules={[
                { required: true, message: 'Please enter longitude' },
                { 
                  pattern: /^-?((1[0-7][0-9]|[1-9]?[0-9])(\.[0-9]+)?|180(\.0+)?)$/, 
                  message: 'Please enter a valid longitude (-180 to 180)' 
                }
              ]}
            >
              <InputNumber
                placeholder="e.g., 28.0473"
                style={{ width: '100%' }}
                step={0.000001}
                precision={6}
                onChange={handleLongitudeChange}
              />
            </Form.Item>
          </div>
          
          <div style={{ flexShrink: 0, minWidth: '140px' }}>
            <Button
              type="dashed"
              icon={<AimOutlined />}
              loading={loadingLocation}
              onClick={useCurrentLocation}
              style={{ width: '100%', height: '32px' }}
            >
              Use My Location
            </Button>
          </div>
        </div>
      </div>

      {formData.latitude && formData.longitude && (
        <div style={{ 
          background: '#f0f9ff', 
          border: '1px solid #91d5ff', 
          borderRadius: '6px', 
          padding: '12px',
          marginTop: '16px'
        }}>
          <Text strong style={{ color: '#1890ff', display: 'block' }}>
            <GlobalOutlined style={{ marginRight: '8px' }} />
            Location Set:
          </Text>
          <Text>{formData.address || 'Address not specified'}</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
            Coordinates: {formData.latitude}, {formData.longitude}
          </Text>
        </div>
      )}
    </div>
  );
};

export default LocationStep;
